import HttpRequest from "@/util/HttpRequest";
import { mergePostContents } from "@/util/DataUtil";

// 캐시 관련 유틸리티 추가
const promiseCache = new Map();

function getCacheKey(actionName, params) {
  return `${actionName}:${JSON.stringify(params)}`;
}

/**
 * API 호출 결과를 캐싱하는 함수
 * @param {string} cacheKey - 캐시 키
 * @param {Function} apiCallFn - API 호출 함수
 * @param {number} memCacheTime - 메모리 캐싱 시간 (밀리초), 기본값 5초
 * @param {boolean} useLocalStorage - 로컬 스토리지 사용 여부, 기본값 false
 * @param {number} localStorageTime - 로컬 스토리지 캐싱 시간 (밀리초), 기본값 5분
 * @returns {Promise} API 호출 결과를 담은 Promise
 */
function cachedApiCall(cacheKey, apiCallFn, memCacheTime = 5000, useLocalStorage = false, localStorageTime = 300000) {
  // 1. 먼저 메모리 캐시 확인
  if (promiseCache.has(cacheKey)) {
    return promiseCache.get(cacheKey);
  }

  // 2. 로컬 스토리지 캐시 확인 (사용하는 경우)
  if (useLocalStorage) {
    const lsKey = `ls_cache_${cacheKey}`;
    const cached = localStorage.getItem(lsKey);

    if (cached) {
      try {
        const { data, expiry } = JSON.parse(cached);
        // 만료 시간 확인
        if (expiry > Date.now()) {
          // 유효한 캐시가 있으면 Promise로 래핑하여 반환
          const cachedPromise = Promise.resolve(data);
          promiseCache.set(cacheKey, cachedPromise);
          return cachedPromise;
        } else {
          // 만료된 캐시는 삭제
          localStorage.removeItem(lsKey);
        }
      } catch (e) {
        // JSON 파싱 오류 시 캐시 삭제
        localStorage.removeItem(lsKey);
      }
    }
  }

  // 3. 캐시 없음, API 호출 실행
  const promise = apiCallFn()
    .then((response) => {
      // 로컬 스토리지에 저장 (사용하는 경우)
      if (useLocalStorage) {
        try {
          const lsKey = `ls_cache_${cacheKey}`;
          const cacheData = {
            data: response,
            expiry: Date.now() + localStorageTime,
          };
          localStorage.setItem(lsKey, JSON.stringify(cacheData));
        } catch (e) {
          // 저장 실패 시 무시 (로컬 스토리지 크기 초과 등의 이유)
          console.warn("Failed to cache in localStorage:", e);
        }
      }
      return response;
    })
    .finally(() => {
      // 메모리 캐시 만료 처리
      setTimeout(() => {
        promiseCache.delete(cacheKey);
      }, memCacheTime);
    });

  // 메모리 캐시에 저장 후 반환
  promiseCache.set(cacheKey, promise);
  return promise;
}

// 조건을 파라미터로 변환하는 재귀 함수
function processCondition(condition, prefix, option) {
  const subConditions = condition.conditions;
  const key = condition.key && condition.key.trim();

  if (key) {
    const operator = condition.operator && `${condition.operator}`.trim();
    const value = condition.value;

    // 배열인 경우 먼저 필터링
    if (Array.isArray(value)) {
      const filteredValues = value.filter(v => v !== undefined && v !== null && `${v}`.trim() !== '');
      if (filteredValues.length === 0) return false;

      if (operator) {
        option.params[`${prefix}[key]`] = key;
        option.params[`${prefix}[operator]`] = operator;
        filteredValues.forEach((v, vIndex) => {
          option.params[`${prefix}[value][${vIndex}]`] = `${v}`.trim();
        });
        if (condition.boolean) {
          option.params[`${prefix}[boolean]`] = condition.boolean;
        }
        return true;
      }
      return false;
    }

    // 배열이 아닌 경우
    if (operator) {
      const opUpper = `${operator}`.toUpperCase();

      // '!=' / '<>' 는 빈 문자열('')도 의미 있는 필터다("빈 값이 아님").
      // 그 외 연산자(=, >, <, LIKE 등)에서 빈 값은 "검색 조건 미입력"으로 보고 제외한다.
      //  - 예: 사업장 미선택 시 field_id = '' 가 전송되어 결과가 0건이 되는 문제 방지
      //  - 빈 값 "매칭"이 필요한 화면은 REGEXP "^$" 처럼 비어있지 않은 값을 가진 연산자로 표현한다.
      const allowEmptyValue = opUpper === "!=" || opUpper === "<>";

      // undefined/null은 "값 미입력"으로 간주하여 항상 제외한다.
      if (value !== undefined && value !== null) {
        const trimmed = `${value}`.trim();
        if (trimmed !== "" || allowEmptyValue) {
          option.params[`${prefix}[key]`] = key;
          option.params[`${prefix}[operator]`] = operator;
          option.params[`${prefix}[value]`] = trimmed;
          if (condition.boolean) {
            option.params[`${prefix}[boolean]`] = condition.boolean;
          }
          return true;
        }
      }
    }
    return false;
  } else if (subConditions && Array.isArray(subConditions)) {
    const boolean = condition.boolean;

    // 먼저 유효한 하위 조건이 있는지 확인
    const tempOption = { params: {} };
    let subIndex = 0;

    for (let i = 0; i < subConditions.length; i++) {
      const subCondition = subConditions[i];
      const subPrefix = `${prefix}[conditions][${subIndex}]`;

      if (processCondition(subCondition, subPrefix, tempOption)) {
        subIndex++;
      }
    }

    // 유효한 조건이 있을 때만 추가
    if (subIndex > 0 && boolean) {
      option.params[`${prefix}[boolean]`] = boolean;
      Object.assign(option.params, tempOption.params);
      return true;
    }
    return false;
  }
  return false;
}


export default {
  async LOAD_BOARD({ commit, state }, params) {
    const boardId = params.boardId;
    const reload = params.reload_board;
    const useCache = params.useCache !== false; // 기본값 true

    // 조회할 게시판 ID가 없을 경우, 종료
    if (!boardId) {
      return;
    }

    if (!state.boardData || state.boardData.board_slug !== boardId || reload) {
      if (useCache) {
        const cacheKey = getCacheKey("LOAD_BOARD", { boardId });
        return cachedApiCall(
          cacheKey,
          () => {
            return new Promise((resolve, reject) => {
              HttpRequest.get(`/api/v1/board/${boardId}`)
                .then((response) => {
                  commit("LOAD_BOARD", response);
                  resolve(response);
                })
                .catch((err) => reject(err));
            });
          },
          600000
        ); // 10분 메모리 캐싱, 로컬 스토리지 사용
      } else {
        return new Promise((resolve, reject) => {
          HttpRequest.get(`/api/v1/board/${boardId}`)
            .then((response) => {
              commit("LOAD_BOARD", response);
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      }
    }
  },

  /**
   * 게시판 목록
   * @params {page, perPage}
   */
  async LOAD_BOARD_LIST({ commit }) {
    const cacheKey = getCacheKey("LOAD_BOARD_LIST", {});

    return cachedApiCall(
      cacheKey,
      () => {
        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/board")
            .then((response) => {
              // mutations 메소드 호출
              commit("LOAD_BOARD_LIST", response);
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      },
      600000
    ); // 10분 메모리 캐싱, 로컬 스토리지 사용
  },

  /**
   * 게시판 종류 불러오기
   * @params {page, perPage}
   */
  async LOAD_BOARD_TYPE_LIST({ commit }) {
    const cacheKey = getCacheKey("LOAD_BOARD_TYPE_LIST", {});

    return cachedApiCall(
      cacheKey,
      () => {
        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/info/board/type")
            .then((response) => {
              // mutations 메소드 호출
              commit("LOAD_BOARD_TYPE_LIST", response);
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      },
      600000
    ); // 10분 메모리 캐싱, 로컬 스토리지 사용
  },

  async SORT_POST_LIST(context, params) {
    const boardId = params.boardId;
    const option = {
      projectId: params.projectId,
      post_sticky_order: params.post_sticky_order,
    };

    const cacheKey = getCacheKey("SORT_POST_LIST", { boardId, ...option });

    return cachedApiCall(
      cacheKey,
      () => {
        return new Promise((resolve, reject) => {
          HttpRequest.post(`/api/v1/board/${boardId}/sort`, option)
            .then((response) => {
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      },
      60000
    ); // 1분 메모리 캐싱
  },

  async CHECK_SAFETY_MANAGER({ commit }, params) {
    const cacheKey = getCacheKey("CHECK_SAFETY_MANAGER", params);

    return cachedApiCall(
      cacheKey,
      () => {
        const option = {
          params: {},
        };

        if (params.boardIds && Array.isArray(params.boardIds) && params.boardIds.length > 0) {
          option.params.boardIds = params.boardIds;
        }
        if (params.fields && Array.isArray(params.fields) && params.fields.length > 0) {
          option.params.fields = params.fields;
        }

        return new Promise((resolve, reject) => {
          HttpRequest.get(`/api/v1/hse/info/check-safety-manager`, option)
            .then((response) => {
              commit("CHECK_SAFETY_MANAGER", response);
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      },
      60000
    ); // 1분 메모리 캐싱
  },

  // 공지사항 게시글 불러오기
  async LOAD_POST_LIST({ commit }, params, useCache = true) {
    const cacheKey = getCacheKey("LOAD_POST_LIST", params);

    if (!useCache) promiseCache.delete(cacheKey);
    return cachedApiCall(cacheKey, () => {
      const boardId = params.boardId;
      const option = {
        params: {
          projectId: params.projectId,
          page: params.page,
          perPage: params.perPage,
          keyword: params.keyword,
          preview: params.preview,
          dateTarget: params.dateTarget,
          dateBegin: params.dateBegin,
          dateEnd: params.dateEnd,
          orderTarget: params.orderTarget,
          orderDirection: params.orderDirection,
          companyIds: params.companyIds,
          employeeCompanyId: params.employeeCompanyId,
          fieldId: params.fieldId,
          withMeta: params.withMeta,
          metaKeys: params.metaKeys,
          onlyMeta: params.onlyMeta,
          withoutRelation: params.withoutRelation,
          withBoardIds: params.withBoardIds,
          postIds: params.postIds,
        },
      };
      if (params.orders && params.orders.length > 0) {
        let index = 0;
        for (let i = 0, order; (order = params.orders[i]); i++) {
          const target = order.target && order.target.trim();
          const direction = order.direction && `${order.direction}`.trim();
          if (target && direction) {
            option.params[`orders[${index}][target]`] = target;
            option.params[`orders[${index}][direction]`] = direction;
            index++;
          }
        }
      }

      // conditions 처리
      if (params.conditions && params.conditions.length > 0) {
        let index = 0;
        for (let i = 0; i < params.conditions.length; i++) {
          const condition = params.conditions[i];
          const prefix = `conditions[${index}]`;

          if (processCondition(condition, prefix, option)) {
            index++;
          }
        }
      }

      return new Promise((resolve, reject) => {
        HttpRequest.get(`/api/v1/board/post/${boardId}`, option)
          .then((response) => {
            const data = response.data;
            if (data && data.length > 0) {
              data.forEach((post) => {
                post.postContents = mergePostContents(post);
                delete post.post_contents;
              });
            }

            commit("LOAD_POST_LIST", response);
            resolve(response);
          })
          .catch((err) => reject(err));
      });
    });
  },

  async LOAD_POST_IDS({ commit }, params) {
    const cacheKey = getCacheKey("LOAD_POST_IDS", params);

    return cachedApiCall(cacheKey, () => {
      const boardId = params.boardId;
      const option = {
        params: {
          projectId: params.projectId,
          keyword: params.keyword,
          preview: params.preview,
          dateTarget: params.dateTarget,
          dateBegin: params.dateBegin,
          dateEnd: params.dateEnd,
          orderTarget: params.orderTarget,
          orderDirection: params.orderDirection,
          companyIds: params.companyIds,
          employeeCompanyId: params.employeeCompanyId,
          fieldId: params.fieldId,
          withBoardIds: params.withBoardIds,
        },
      };
      if (params.orders && params.orders.length > 0) {
        let index = 0;
        for (let i = 0, order; (order = params.orders[i]); i++) {
          const target = order.target && order.target.trim();
          const direction = order.direction && `${order.direction}`.trim();
          if (target && direction) {
            option.params[`orders[${index}][target]`] = target;
            option.params[`orders[${index}][direction]`] = direction;
            index++;
          }
        }
      }

      if (params.conditions && params.conditions.length > 0) {
        let index = 0;
        for (let i = 0, condition; (condition = params.conditions[i]); i++) {
          const sub1Conditions = condition.conditions;
          const key = condition.key && condition.key.trim();
          if (key) {
            const operator = condition.operator && `${condition.operator}`.trim();
            const value = condition.value && `${condition.value}`.trim();
            if (operator && (operator === "!=" || (operator !== "!=" && (value === 0 || value)))) {
              option.params[`conditions[${index}][key]`] = key;
              option.params[`conditions[${index}][operator]`] = operator;
              option.params[`conditions[${index}][value]`] = value;
              if (condition.boolean) option.params[`conditions[${index}][boolean]`] = condition.boolean;
              index++;
            }
          } else if (sub1Conditions) {
            const boolean = condition.boolean;
            let subIndex = 0;

            if (boolean) {
              option.params[`conditions[${index}][boolean]`] = boolean;
              for (let j = 0, sub1Condition; (sub1Condition = sub1Conditions[j]); j++) {
                const sub2Conditions = sub1Condition.conditions;
                const key = sub1Condition.key && sub1Condition.key.trim();
                const operator = sub1Condition.operator && `${sub1Condition.operator}`.trim();
                const value = sub1Condition.value && `${sub1Condition.value}`.trim();
                const boolean = sub1Condition.boolean;
                option.params[`conditions[${index}][conditions][${subIndex}][boolean]`] = boolean;
                if (operator && (operator === "!=" || (operator !== "!=" && (value === 0 || value)))) {
                  option.params[`conditions[${index}][conditions][${subIndex}][key]`] = key;
                  option.params[`conditions[${index}][conditions][${subIndex}][operator]`] = operator;
                  option.params[`conditions[${index}][conditions][${subIndex}][value]`] = value;
                  subIndex++;
                } else if (sub2Conditions) {
                  for (let k = 0, sub2Condition; (sub2Condition = sub2Conditions[k]); k++) {
                    const key = sub2Condition.key && sub2Condition.key.trim();
                    const operator = sub2Condition.operator && `${sub2Condition.operator}`.trim();
                    const value = sub2Condition.value && `${sub2Condition.value}`.trim();
                    if (boolean === "OR") option.params[`conditions[${index}][conditions][${subIndex}][boolean]`] = boolean;
                    option.params[`conditions[${index}][conditions][${subIndex}][key]`] = key;
                    option.params[`conditions[${index}][conditions][${subIndex}][operator]`] = operator;
                    option.params[`conditions[${index}][conditions][${subIndex}][value]`] = value;
                  }
                  subIndex++;
                }
              }
            }
            index++;
          }
        }
      }

      return new Promise((resolve, reject) => {
        HttpRequest.get(`/api/v1/board/post/${boardId}/ids`, option)
          .then((response) => {
            const data = response.data;
            if (data && data.length > 0) {
              data.forEach((post) => {
                post.postContents = mergePostContents(post);
                delete post.post_contents;
              });
            }

            commit("LOAD_POST_IDS", response);
            resolve(response);
          })
          .catch((err) => reject(err));
      });
    });
  },

  async LOAD_POST({ commit }, params) {
    const boardId = params.boardId;
    const postId = params.postId;

    const cacheKey = getCacheKey("LOAD_POST", { boardId, postId });

    return cachedApiCall(
      cacheKey,
      () => {
        return new Promise((resolve, reject) => {
          HttpRequest.get(`/api/v1/board/post/${boardId}/${postId}`)
            .then((response) => {
              commit("LOAD_POST", response);
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      },
      10000
    ); // 10초 메모리 캐싱
  },

  /**
   * 위험성평가 D/B 조회
   * @param commit
   * @param params
   * @returns {Promise<unknown>}
   * @constructor
   */
  async LOAD_RISK_ASSESSMENT({ commit }, params) {
    const cacheKey = getCacheKey("LOAD_RISK_ASSESSMENT", params);

    return cachedApiCall(
      cacheKey,
      () => {
        const option = {
          params: {
            board_ids: params.boardIds,
            per_page: params.perPage,
            page: params.page,
            sort_by: params.sortBy, // 정렬기준 : risk_level, created_at, post_title, field_name, board_id, classification, cause
            sort_direction: params.sortDirection || "desc", // 정렬방향 : asc, desc
            category1: params.category1, // 대분류
            category2: params.category2, // 중분류
            category3: params.category3, // 소분류
            field_name: params.fieldName, // 사업장명
            important_risk: params.importantRisk, // 중요위험
            work_type: params.workType, // 공정분류표 구분
            classification: params.classification, // 위험성평가표 분류
            cause: params.cause, // 위험성평가표 원인
            keyword: params.keyword,
            field_id: params.fieldId,
            project_id: params.projectId,
            date_from: params.dateFrom,
            date_to: params.dateTo,
            min_risk: params.minRisk,
            max_risk: params.maxRisk,
            is_parent: params.isParent ? params.isParent : undefined,
          },
        };

        return new Promise((resolve, reject) => {
          HttpRequest.get(`/api/v1/board/post/risk-assessment`, option)
            .then((response) => {
              commit("LOAD_RISK_ASSESSMENT", response);
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      },
      600000
    ); // 10분 메모리 캐싱, 로컬 스토리지 사용
  },

  /**
   * 361 Viewer.vue 페이지에서 사용한다
   * @param commit
   * @param params
   * @returns {Promise<unknown>}
   * @constructor
   */
  // eslint-disable-next-line no-unused-vars
  async LOAD_361_SUMMARY({ commit }, params) {
    const cacheKey = getCacheKey("LOAD_361_SUMMARY", params);

    return cachedApiCall(
      cacheKey,
      () => {
        const option = {
          params: {
            projectId: params.projectId,
            targetYear: params.targetYear,
            targetMonth: params.targetMonth,
            fieldId: params.fieldId,
            companyIds: params.companyIds,
          },
        };

        return new Promise((resolve, reject) => {
          HttpRequest.get(`/api/v1/summary/post/361`, option)
            .then((response) => {
              // commit("LOAD_361_SUMMARY", response);
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      },
      600000
    ); // 10분 메모리 캐싱, 로컬 스토리지 사용
  },

  async LOAD_452_SUMMARY(context, params) {
    const cacheKey = getCacheKey("LOAD_452_SUMMARY", params);

    return cachedApiCall(
      cacheKey,
      () => {
        const option = {
          params: {
            projectId: params.projectId,
            targetYear: params.targetYear,
            targetMonth: params.targetMonth,
            fieldId: params.fieldId,
            companyIds: params.companyIds,
          },
        };

        return new Promise((resolve, reject) => {
          HttpRequest.get(`/api/v1/summary/post/452`, option)
            .then((response) => {
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      },
      600000
    ); // 10분 메모리 캐싱, 로컬 스토리지 사용
  },

  async LOAD_COMMENT_LIST({ commit }, params) {
    const cacheDisabled = params.cacheDisabled;
    const boardSlug = params.boardSlug;
    const postId = params.postId;

    if (cacheDisabled) {
      return new Promise((resolve, reject) => {
        HttpRequest.get(`/api/v1/board/comment/${boardSlug}/${postId}`)
          .then((response) => {
            commit("LOAD_COMMENT_LIST", response);
            resolve(response);
          })
          .catch((err) => reject(err));
      });
    } else {
      const cacheKey = getCacheKey("LOAD_COMMENT_LIST", { boardSlug, postId });

      return cachedApiCall(
        cacheKey,
        () => {
          return new Promise((resolve, reject) => {
            HttpRequest.get(`/api/v1/board/comment/${boardSlug}/${postId}`)
              .then((response) => {
                commit("LOAD_COMMENT_LIST", response);
                resolve(response);
              })
              .catch((err) => reject(err));
          });
        },
        60000
      ); // 1분 메모리 캐싱
    }
  },

  async DELETE_POST(context, params) {
    const boardId = params.boardId;
    const postId = params.postId;

    return new Promise((resolve, reject) => {
      HttpRequest.delete(`/api/v1/board/post/${boardId}/${postId}`)
        .then((response) => {
          resolve(response);
        })
        .catch((err) => reject(err));
    });
  },

  async CREATE_POST(context, params) {
    const option = {
      title: params.title,
      content: params.content,
      parentId: params.parentId,
      uploads: params.uploads,
      projectId: params.projectId,
      projectOrderId: params.projectOrderId, //orderId를 전송할 경우 반드시 projectId도 같이 보내야 한다.
    };

    const boardId = params.boardId;
    return new Promise((resolve, reject) => {
      HttpRequest.put(`/api/v1/board/post/${boardId}/`, option)
        .then((response) => {
          resolve(response);
        })
        .catch((err) => reject(err));
    });
  },

  async UPDATE_POST(context, params) {
    const option = {
      title: params.title,
      content: params.content,
      uploads: params.uploads,
      projectId: params.projectId,
      projectOrderId: params.projectOrderId, //orderId를 전송할 경우 반드시 projectId도 같이 보내야 한다.
    };

    if (params.approvalLine) option.approvalLine = JSON.stringify(params.approvalLine);
    if (params.documentCompanyCodes) option.documentCompanyCodes = params.documentCompanyCodes;

    const boardId = params.boardId;
    const postId = params.postId;

    return new Promise((resolve, reject) => {
      HttpRequest.post(`/api/v1/board/post/${boardId}/${postId}`, option)
        .then((response) => {
          resolve(response);
        })
        .catch((err) => reject(err));
    });
  },

  async ADD_COMMENT(context, params) {
    const option = {
      parentId: params.parentId,
      content: params.comment,
    };

    // 부모 댓글 ID가 없을 경우 변수 삭제
    if (!option.parentId || option.parentId === 0 || option.parentId === "0") {
      delete option.parentId;
    }

    const boardSlug = params.boardSlug;
    const postId = params.postId;

    return new Promise((resolve, reject) => {
      HttpRequest.put(`/api/v1/board/comment/${boardSlug}/${postId}`, option)
        .then((response) => {
          resolve(response);
        })
        .catch((err) => reject(err));
    });
  },

  async UPDATE_COMMENT({ commit }, params) {
    const option = {
      content: params.comment,
    };

    const boardSlug = params.boardSlug;
    const postId = params.postId;
    const commentId = params.commentId;

    return new Promise((resolve, reject) => {
      HttpRequest.post(`/api/v1/board/comment/${boardSlug}/${postId}/${commentId}`, option)
        .then((response) => {
          commit("UPDATE_COMMENT", response);
          resolve(response);
        })
        .catch((err) => reject(err));
    });
  },

  async DELETE_COMMENT({ commit }, params) {
    const boardSlug = params.boardSlug;
    const postId = params.postId;
    const commentId = params.commentId;

    return new Promise((resolve, reject) => {
      HttpRequest.delete(`/api/v1/board/comment/${boardSlug}/${postId}/${commentId}`)
        .then((response) => {
          commit("DELETE_COMMENT", response);
          resolve(response);
        })
        .catch((err) => reject(err));
    });
  },

  async LOAD_PHOTO_LIST({ commit }, params) {
    const cacheKey = getCacheKey("LOAD_PHOTO_LIST", params);

    return cachedApiCall(
      cacheKey,
      () => {
        return new Promise((resolve, reject) => {
          HttpRequest.get(`/api/v1/photo`)
            .then((response) => {
              commit("LOAD_PHOTO_LIST", response);
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      },
      60000
    ); // 1분 메모리 캐싱
  },

  async DELETE_PHOTO({ commit }, params) {
    const fileId = params.fileId;

    return new Promise((resolve, reject) => {
      HttpRequest.delete(`/api/v1/photo/${fileId}`)
        .then((response) => {
          commit("DELETE_PHOTO", response);
          resolve(response);
        })
        .catch((err) => reject(err));
    });
  },

  async DELETE_PHOTO_LIST({ commit }, params) {
    const option = {
      fileIds: params.fileIds,
    };

    return new Promise((resolve, reject) => {
      HttpRequest.post("/api/v1/photo/delete", option)
        .then((response) => {
          commit("DELETE_PHOTO_LIST", response);
          resolve(response);
        })
        .catch((err) => reject(err));
    });
  },

  /** 게시판 관련 첨부파일 목록 조회 */
  async LOAD_FILE_LIST({ commit }, params) {
    const boardSlug = params.boardSlug;
    const cacheKey = getCacheKey("LOAD_FILE_LIST", params);

    return cachedApiCall(
      cacheKey,
      () => {
        const option = {
          boardSlug,
          params,
        };

        return new Promise((resolve, reject) => {
          HttpRequest.get(`/api/v1/board/${boardSlug}/attachment`, option)
            .then((response) => {
              commit("LOAD_FILE_LIST", response);
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      },
      600000
    ); // 10분 메모리 캐싱, 로컬 스토리지 사용
  },

  /** 게시판 관련 첨부파일 추가 */
  async UPLOAD_FILE_LIST({ commit }, params) {
    const boardSlug = params.boardSlug;

    const option = {
      uploads: JSON.stringify(params.uploads),
    };

    return new Promise((resolve, reject) => {
      HttpRequest.post(`/api/v1/board/${boardSlug}/upload`, option)
        .then((response) => {
          commit("UPLOAD_FILE_LIST", response);
          resolve(response);
        })
        .catch((err) => reject(err));
    });
  },

  /** 게시판 관련 첨부파일 제거 */
  async REMOVE_FILE_LIST({ commit }, params) {
    const boardSlug = params.boardSlug;

    const option = {
      fileIds: JSON.stringify(params.fileIds),
    };

    return new Promise((resolve, reject) => {
      HttpRequest.put(`/api/v1/board/${boardSlug}/remove-attachment`, option)
        .then((response) => {
          commit("UPLOAD_FILE_LIST", response);
          resolve(response);
        })
        .catch((err) => reject(err));
    });
  },

  /** 게시판 관련 첨부파일 다운로드 */
  async DOWNLOAD_FILE({ commit }, params) {
    const boardSlug = params.boardSlug;
    const option = {
      fileId: params.fileId,
    };

    return new Promise((resolve, reject) => {
      HttpRequest.get(`/api/v1/board/${boardSlug}/download`, option)
        .then((response) => {
          commit("DOWNLOAD_FILE", response);
          resolve(response);
        })
        .catch((err) => reject(err));
    });
  },

  // 게시글 숨김 기능 관련 actions

  /**
   * 숨겨진 게시글 목록 조회
   * @param {Object} context - Vuex context
   * @param {Object} params - 요청 파라미터
   * @param {number} params.boardId - 게시판 ID
   * @param {number} [params.page=1] - 페이지 번호
   * @param {number} [params.perPage=10] - 페이지당 항목 수
   * @param {string} [params.orderDirection='desc'] - 정렬 방향
   * @returns {Promise} API 호출 결과
   */
  async LOAD_HIDDEN_POST_LIST({ commit }, params) {
    const boardId = params.boardId;
    const cacheKey = getCacheKey("LOAD_HIDDEN_POST_LIST", params);

    return cachedApiCall(
      cacheKey,
      () => {
        const option = {
          params: {
            page: params.page || 1,
            perPage: params.perPage || 10,
            boardId: params.boardId,
            orderDirection: params.orderDirection || "desc",
          },
        };

        return new Promise((resolve, reject) => {
          HttpRequest.get(`/api/v1/board/post/${boardId}/hidden`, option)
            .then((response) => {
              commit("LOAD_HIDDEN_POST_LIST", response);
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      },
      60000
    ); // 1분 메모리 캐싱
  },

  /**
   * 게시글 숨김 처리
   * @param {Object} context - Vuex context
   * @param {Object} params - 요청 파라미터
   * @param {number} params.boardId - 게시판 ID
   * @param {number} params.postId - 게시글 ID
   * @returns {Promise} API 호출 결과
   */
  async HIDE_POST({ commit }, params) {
    const boardId = params.boardId;
    const postId = params.postId;

    return new Promise((resolve, reject) => {
      HttpRequest.post(`/api/v1/board/post/${boardId}/${postId}/hide`)
        .then((response) => {
          commit("HIDE_POST", { post_id: postId, hidden_at: response.hidden_at });
          resolve(response);
        })
        .catch((err) => reject(err));
    });
  },

  /**
   * 게시글 숨김 해제
   * @param {Object} context - Vuex context
   * @param {Object} params - 요청 파라미터
   * @param {number} params.boardId - 게시판 ID
   * @param {number} params.postId - 게시글 ID
   * @returns {Promise} API 호출 결과
   */
  async UNHIDE_POST({ commit }, params) {
    const boardId = params.boardId;
    const postId = params.postId;

    return new Promise((resolve, reject) => {
      HttpRequest.delete(`/api/v1/board/post/${boardId}/${postId}/hide`)
        .then((response) => {
          commit("UNHIDE_POST", { post_id: postId });
          resolve(response);
        })
        .catch((err) => reject(err));
    });
  },

  /**
   * 게시글 숨김 상태 확인
   * @param {Object} context - Vuex context
   * @param {Object} params - 요청 파라미터
   * @param {number} params.boardId - 게시판 ID
   * @param {number} params.postId - 게시글 ID
   * @returns {Promise} API 호출 결과
   */
  async LOAD_POST_HIDDEN_STATUS({ commit }, params) {
    const boardId = params.boardId;
    const postId = params.postId;
    const cacheKey = getCacheKey("LOAD_POST_HIDDEN_STATUS", params);

    return cachedApiCall(
      cacheKey,
      () => {
        return new Promise((resolve, reject) => {
          HttpRequest.get(`/api/v1/board/post/${boardId}/${postId}/hidden-status`)
            .then((response) => {
              commit("LOAD_POST_HIDDEN_STATUS", response);
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      },
      30000
    ); // 30초 메모리 캐싱
  },

  /**
   * 일괄 게시글 숨김 처리
   * @param {Object} context - Vuex context
   * @param {Object} params - 요청 파라미터
   * @param {number} params.boardId - 게시판 ID
   * @param {number[]} params.postIds - 게시글 ID 배열
   * @returns {Promise} API 호출 결과
   */
  async BULK_HIDE_POSTS({ commit }, params) {
    const boardId = params.boardId;
    const option = {
      post_ids: params.postIds,
    };

    return new Promise((resolve, reject) => {
      HttpRequest.post(`/api/v1/board/post/${boardId}/bulk-hide`, option)
        .then((response) => {
          commit("BULK_HIDE_POSTS", response);
          resolve(response);
        })
        .catch((err) => reject(err));
    });
  },

  /**
   * 일괄 게시글 숨김 해제
   * @param {Object} context - Vuex context
   * @param {Object} params - 요청 파라미터
   * @param {number} params.boardId - 게시판 ID
   * @param {number[]} params.postIds - 게시글 ID 배열
   * @returns {Promise} API 호출 결과
   */
  async BULK_UNHIDE_POSTS({ commit }, params) {
    const boardId = params.boardId;
    const option = {
      post_ids: params.postIds,
    };

    return new Promise((resolve, reject) => {
      HttpRequest.post(`/api/v1/board/post/${boardId}/bulk-unhide`, option)
        .then((response) => {
          commit("BULK_UNHIDE_POSTS", { ...response, unhidden_post_ids: params.postIds });
          resolve(response);
        })
        .catch((err) => reject(err));
    });
  },

  /**
   * 특정 조건을 만족하는 게시글 목록 조회 (has-value API)
   * @param {Object} context - Vuex context
   * @param {Object} params - 요청 파라미터
   * @param {number} params.boardId - 게시판 ID
   * @param {string[]} params.options - 조회할 필드 배열
   * @param {Array<Array<string>>} params.values - 검색할 값들의 2차원 배열
   * @param {string} params.withApproval - 결재 정보 포함 여부
   * @param {string} params.withApprovalElement - 결재 라인 요소 포함 여부
   * @param {number[]} params.approvalStatusIds - 결재 상태 ID 배열
   * @param {number} params.fieldId - 사업장 ID
   * @returns {Promise} API 호출 결과
   */
  async LOAD_POST_HAS_VALUE({ commit }, params) {
    const cacheKey = getCacheKey("LOAD_POST_HAS_VALUE", params);

    return cachedApiCall(
      cacheKey,
      () => {
        const option = {
          boardId: params.boardId,
          options: params.options,
          values: params.values,
          withApproval: params.withApproval,
          withApprovalElement: params.withApprovalElement,
          approvalStatusIds: params.approvalStatusIds,
          fieldId: params.fieldId,
        };

        return new Promise((resolve, reject) => {
          HttpRequest.post("/api/v1/post/has-value", option)
            .then((response) => {
              commit("LOAD_POST_HAS_VALUE", response);
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      },
      60000
    ); // 1분 메모리 캐싱
  },
};
